import {
    KerberosAuthResponse,
    KerberosOAuth2TokenResponse,
    KerberosUserProfile,
} from '@/types/types';
import {
    IAuthService,
    IAuthUser,
    IAuthEvents,
    AuthEventCallback,
    AuthEventType,
} from './IAuthService';

export interface KerberosAuthConfig {
    domain: string;
    loginPath?: string;
    tokenRefreshIntervalMs?: number;
}

const DEFAULT_REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const DEFAULT_LOGIN_PATH = '/api/idp/login';
const LEGACY_LOGIN_PATH = '/login';
const OAUTH2_TOKEN_PATH = '/oauth2/token';
const USER_STORAGE_KEY = 'kerberos_user';

interface KerberosUser extends IAuthUser {
    profile: KerberosUserProfile & {
        sub: string;
        email?: string | null;
    };
}

class KerberosAuthService implements IAuthService {
    private readonly config: KerberosAuthConfig;

    private currentUser: KerberosUser | null = null;

    private refreshTimer: ReturnType<typeof setInterval> | null = null;

    private eventListeners: Map<AuthEventType, Set<AuthEventCallback>> = new Map();

    constructor(config: KerberosAuthConfig) {
        this.config = {
            ...config,
            loginPath: config.loginPath || DEFAULT_LOGIN_PATH,
            tokenRefreshIntervalMs: config.tokenRefreshIntervalMs || DEFAULT_REFRESH_INTERVAL_MS,
        };
        this.loadUserFromStorage();
    }

    private resolvedLoginPath: string | null = null;

    private async fetchLogin(): Promise<Response> {
        const path = this.resolvedLoginPath || this.config.loginPath;
        const response = await fetch(`${this.config.domain}${path}`, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });

        if (response.status === 404 && !this.resolvedLoginPath && path !== LEGACY_LOGIN_PATH) {
            this.resolvedLoginPath = LEGACY_LOGIN_PATH;
            return fetch(`${this.config.domain}${LEGACY_LOGIN_PATH}`, {
                method: 'GET',
                credentials: 'include',
                headers: { Accept: 'application/json' },
            });
        }

        if (!this.resolvedLoginPath) {
            this.resolvedLoginPath = path;
        }

        return response;
    }

    private parseJwt(token: string): (Omit<KerberosUserProfile, 'groups'> & { groups?: string | string[] }) | null {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
                    .join(''),
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Failed to parse JWT:', error);
            return null;
        }
    }

    private async fetchOAuth2Token(): Promise<string> {
        const response = await fetch(`${this.config.domain}${OAUTH2_TOKEN_PATH}`, {
            method: 'POST',
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            throw new Error(`OAuth2 token exchange failed: ${response.status} ${response.statusText}`);
        }

        const data: KerberosOAuth2TokenResponse = await response.json();
        const token = data.access_token || data.id_token;
        if (!token) {
            throw new Error('OAuth2 token response missing access_token');
        }
        return token;
    }

    private async resolveAccessToken(loginBody: KerberosAuthResponse): Promise<string> {
        if (loginBody.jwt) {
            return loginBody.jwt;
        }
        return this.fetchOAuth2Token();
    }

    private createUserFromToken(accessToken: string): KerberosUser {
        const parsed = this.parseJwt(accessToken);
        if (!parsed) {
            throw new Error('Invalid JWT in response');
        }

        const groups = Array.isArray(parsed.groups)
            ? parsed.groups.join(',')
            : parsed.groups;

        const profile = {
            ...parsed,
            groups,
        };

        return {
            access_token: accessToken,
            id_token: accessToken,
            profile,
            expired: false,
            expires_at: profile.exp,
        };
    }

    private isTokenExpired(user: KerberosUser): boolean {
        const now = Math.floor(Date.now() / 1000);
        return user.expires_at <= now + 60;
    }

    private loadUserFromStorage(): void {
        try {
            const stored = sessionStorage.getItem(USER_STORAGE_KEY);
            if (stored) {
                const user = JSON.parse(stored) as KerberosUser;
                if (!this.isTokenExpired(user)) {
                    this.currentUser = user;
                } else {
                    sessionStorage.removeItem(USER_STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error('Failed to load user from storage:', error);
            sessionStorage.removeItem(USER_STORAGE_KEY);
        }
    }

    private saveUserToStorage(user: KerberosUser): void {
        try {
            sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
        } catch (error) {
            console.error('Failed to save user to storage:', error);
        }
    }

    private clearUserFromStorage(): void {
        sessionStorage.removeItem(USER_STORAGE_KEY);
    }

    private emitEvent(eventType: AuthEventType, data?: unknown): void {
        const listeners = this.eventListeners.get(eventType);
        if (listeners) {
            listeners.forEach((callback) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in ${eventType} listener:`, error);
                }
            });
        }
    }

    private startRefreshTimer(): void {
        this.stopRefreshTimer();
        this.refreshTimer = setInterval(async () => {
            try {
                await this.refreshToken();
            } catch (error) {
                console.error('Token refresh failed:', error);
                this.emitEvent('silentRenewError', error);
            }
        }, this.config.tokenRefreshIntervalMs);
    }

    private stopRefreshTimer(): void {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    public addEventHandler(eventType: AuthEventType, callback: AuthEventCallback): void {
        if (!this.eventListeners.has(eventType)) {
            this.eventListeners.set(eventType, new Set());
        }
        this.eventListeners.get(eventType)!.add(callback);
    }

    public removeEventHandler(eventType: AuthEventType, callback: AuthEventCallback): void {
        this.eventListeners.get(eventType)?.delete(callback);
    }

    public get events(): IAuthEvents {
        return {
            addSilentRenewError: (callback: AuthEventCallback) => this.addEventHandler('silentRenewError', callback),
            addUserSignedOut: (callback: AuthEventCallback) => this.addEventHandler('userSignedOut', callback),
            addUserSessionChanged: (callback: AuthEventCallback) => this.addEventHandler('userSessionChanged', callback),
            addUserLoaded: (callback: AuthEventCallback) => this.addEventHandler('userLoaded', callback),
            removeSilentRenewError: (callback: AuthEventCallback) => this.removeEventHandler('silentRenewError', callback),
            removeUserSignedOut: (callback: AuthEventCallback) => this.removeEventHandler('userSignedOut', callback),
            removeUserSessionChanged: (callback: AuthEventCallback) => this.removeEventHandler('userSessionChanged', callback),
            removeUserLoaded: (callback: AuthEventCallback) => this.removeEventHandler('userLoaded', callback),
        };
    }

    public async login(): Promise<IAuthUser> {
        const response = await this.fetchLogin();

        if (!response.ok) {
            throw new Error(`Kerberos authentication failed: ${response.status} ${response.statusText}`);
        }

        const data: KerberosAuthResponse = await response.json();
        const accessToken = await this.resolveAccessToken(data);
        const user = this.createUserFromToken(accessToken);

        this.currentUser = user;
        this.saveUserToStorage(user);
        this.startRefreshTimer();
        this.emitEvent('userLoaded', user);

        return user;
    }

    public async refreshToken(): Promise<IAuthUser> {
        const accessToken = await this.fetchOAuth2Token();
        const user = this.createUserFromToken(accessToken);
        
        this.currentUser = user;
        this.saveUserToStorage(user);
        this.emitEvent('userSessionChanged', user);
        
        return user;
    }

    public async getUser(): Promise<IAuthUser | null> {
        if (this.currentUser && !this.isTokenExpired(this.currentUser)) {
            return this.currentUser;
        }
        
        this.loadUserFromStorage();
        
        if (this.currentUser && !this.isTokenExpired(this.currentUser)) {
            return this.currentUser;
        }
        
        return null;
    }

    public async getAccessToken(): Promise<string | null> {
        const user = await this.getUser();
        return user?.access_token || null;
    }

    public async logout(): Promise<void> {
        this.stopRefreshTimer();
        this.currentUser = null;
        this.clearUserFromStorage();
        this.emitEvent('userSignedOut');
        this.emitEvent('userUnloaded');
    }

    public async removeUser(): Promise<void> {
        await this.logout();
    }

    public async clearStaleState(): Promise<void> {
        if (this.currentUser && this.isTokenExpired(this.currentUser)) {
            this.currentUser = null;
            this.clearUserFromStorage();
        }
    }

    public async revokeAccessToken(): Promise<void> {
        // Not supported by domain-idp-service
    }

    public isAuthenticated(): boolean {
        return this.currentUser !== null && !this.isTokenExpired(this.currentUser);
    }

    public async handleRedirectCallback(): Promise<IAuthUser> {
        return this.login();
    }

    public async handleLogoutCallback(): Promise<void> {
        // No-op for Kerberos
    }

    public destroy(): void {
        this.stopRefreshTimer();
        this.eventListeners.clear();
    }
}

export default KerberosAuthService;
